class LazyVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video')

    if (!this.video) {
      return
    }

    this.promise = Promise.resolve()
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.promise = this.video.play()
          .catch(() => {})
      } else {
        this.promise.then(() => {
          this.video.pause()
        })
      }
    })

    this.observer.observe(this)
  }

  disconnectedCallback() {
    this.observer.disconnect()
  }
}

customElements.define('lazy-video', LazyVideo)

class MultiVideo extends HTMLElement {
  static observedAttributes = ["src-480", "src-720", "src-1080", "muted", "loop", "poster"];

  #initialized = false;
  #observer = null;
  #video = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; }
        video { display: block; width: 100%; height: 100%; object-fit: var(--lazy-video-fit, cover); }
      </style>
      <video part="video" playsinline></video>
    `;
    this.#video = this.shadowRoot.querySelector("video");
  }

  connectedCallback() {
    this.#syncBoolAttr("muted");
    this.#syncBoolAttr("loop");
    if (this.hasAttribute("poster")) {
      this.#video.poster = this.getAttribute("poster");
    }

    if (!this.#initialized) {
      this.#pickSource();
      this.#initialized = true;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.#video.play().catch(() => {});
        } else {
          this.#video.pause();
        }
      },
      { threshold: parseFloat(this.getAttribute("threshold") || "0") }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#video.pause();
    this.#video.removeAttribute("src");
    this.#video.load();
    this.#initialized = false;
  }

  attributeChangedCallback(name, _old, val) {
    if (!this.#video) return;
    if (name === "muted" || name === "loop") {
      this.#video[name] = this.hasAttribute(name);
    } else if (name === "poster") {
      this.#video.poster = val ?? "";
    } else if (this.#initialized && (name === "src-480" || name === "src-720" || name === "src-1080")) {
      this.#pickSource();
    }
  }

  #pickSource() {
    const w = this.clientWidth * (window.devicePixelRatio || 1);
    let src;
    if (w <= 640) {
      src = this.getAttribute("src-480");
    } else if (w <= 960) {
      src = this.getAttribute("src-720");
    } else {
      src = this.getAttribute("src-1080");
    }
    src = src
      || this.getAttribute("src-1080")
      || this.getAttribute("src-720")
      || this.getAttribute("src-480");

    if (!src) return;

    this.#video.src = src;
    this.#video.load();

    this.dispatchEvent(new CustomEvent("sourceselected", {
      detail: { src, effectiveWidth: this.clientWidth },
      bubbles: true,
    }));
  }

  get video() { return this.#video; }

  #syncBoolAttr(name) {
    this.#video[name] = this.hasAttribute(name);
  }
}

customElements.define("multi-video", MultiVideo);
